import { memo, useCallback, useEffect, useMemo, useState } from "react";
import * as LucideIcons from "lucide-react";
import { LucideIcon } from "lucide-react";
import { FontWrapper } from "../common/FontWrapper";
import { EditModeProvider } from "../../contexts/EditModeContext";
import { EditModePanel } from "../common/EditModePanel";
import { ScientificBasisInfo } from "../common/ScientificBasisInfo";
import { EditableText } from "../common/EditableText";
import { SmishingChat } from "../common/SmishingChat";
import { SlackChat } from "../common/SlackChat";
import { WhatsAppChat } from "../common/WhatsAppChat";
import { TelegramChat } from "../common/TelegramChat";
import { TeamsChat } from "../common/TeamsChat";
import { InstagramChat } from "../common/InstagramChat";
import { LinkedInChat } from "../common/LinkedInChat";
import { useIsMobile } from "../ui/use-mobile";
import { CallToAction } from "../ui/CallToAction";
import { deepMerge } from "../../utils/deepMerge";
import { CommentPinsOverlay } from "../ui/comment-pins-overlay";
import { DecorativeSceneIcon } from "../../presentation/DecorativeSceneIcon";
import { CARD_PHONE_FRAME_CLASS, CARD_PHONE_STAGE_CLASS, sceneMainClassName, sceneSubtitleClassName, sceneTitleClassName, useCardSceneLayout } from "../../presentation/cardSceneLayout";
import type { SmishingSceneConfig } from "../configs/educationConfigs";

interface SmishingSceneProps {
  config: SmishingSceneConfig;
  appConfig?: any;
  onNextSlide?: () => void;
  sceneId?: string | number;
  selectedLanguage?: string;
  onChatCompleted?: (completed: boolean) => void;
}

const DEFAULT_CONTAINER_CLASS = "flex flex-col items-center justify-center h-full text-center";

const getIconComponent = (iconName?: string): LucideIcon => {
  if (!iconName) return LucideIcons.MessageSquare;
  const camelCaseName = iconName
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  if (camelCaseName in LucideIcons) {
    return LucideIcons[camelCaseName as keyof typeof LucideIcons] as LucideIcon;
  }

  return LucideIcons.MessageSquare;
};

const resolveText = (value?: string | { mobile?: string; desktop?: string }, isMobile?: boolean) => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  return isMobile ? value.mobile : value.desktop;
};

export const SmishingScene = memo(function SmishingScene({
  config,
  appConfig,
  onNextSlide,
  sceneId,
  selectedLanguage,
  onChatCompleted
}: SmishingSceneProps) {
  const [editChanges, setEditChanges] = useState<Partial<SmishingSceneConfig>>({});
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [configKey] = useState(0);
  const [hasCompletedChat, setHasCompletedChat] = useState(false);
  const isMobile = useIsMobile();
  const layout = useCardSceneLayout();

  const currentConfig = useMemo(() => {
    return deepMerge(config, editChanges);
  }, [config, editChanges]);

  useEffect(() => {
    onChatCompleted?.(hasCompletedChat);
  }, [hasCompletedChat, onChatCompleted]);

  const IconComponent = useMemo(() => {
    return getIconComponent(currentConfig.iconName);
  }, [currentConfig.iconName]);

  const handleSave = useCallback((newConfig: any) => {
    setEditChanges(newConfig);
  }, []);

  const microlearningId = (appConfig as any)?.microlearning_id || "unknown";
  const startLabel = resolveText(currentConfig.callToActionText, isMobile) || "Start chat";
  const linkWarningTitle = currentConfig.texts?.linkWarningTitle;
  const linkWarningBody = currentConfig.texts?.linkWarningBody;
  const otpTexts = {
    codeLabel: currentConfig.texts?.otpCodeLabel,
    codeSuffix: currentConfig.texts?.otpCodeSuffix,
    backLabel: currentConfig.texts?.otpBackLabel,
    backHint: currentConfig.texts?.otpBackHint,
  };
  const landingPageTexts = {
    backLabel: currentConfig.texts?.landingPageBackLabel,
  };
  const uiTexts = (currentConfig as any).uiTexts as Record<string, string> | undefined;
  const hideSceneChrome = layout.isCard && !isInEditMode;
  const phoneHeightClass = layout.isCard
    ? CARD_PHONE_FRAME_CLASS
    : "h-[520px] max-h-[75vh] sm:h-[560px] sm:max-h-[80vh]";

  return (
    <EditModeProvider
      key={configKey}
      initialConfig={currentConfig}
      sceneId={sceneId?.toString()}
      onSave={handleSave}
      onEditModeChange={setIsInEditMode}
    >
      <EditModePanel sceneId={sceneId} sceneLabel={(currentConfig as any)?.title} appConfig={appConfig} />
      <ScientificBasisInfo
        config={currentConfig}
        sceneType={(currentConfig as any)?.scene_type || "smishing_simulation"}
      />
      <FontWrapper className={layout.isCard ? "flex h-full min-h-0 flex-1 flex-col" : undefined}>
        <main
          className={sceneMainClassName(DEFAULT_CONTAINER_CLASS, layout, layout.isCard ? "relative h-full min-h-0 flex-1 overflow-hidden" : "relative")}
          role="main"
          aria-labelledby="smishing-scene-title"
          data-scene-type={(config as any)?.scene_type || "smishing_simulation"}
          data-scene-id={sceneId as any}
          data-testid="scene-smishing"
          data-comment-surface="true"
        >
          <DecorativeSceneIcon className={`mb-1 sm:mb-2 p-3 ${isInEditMode ? "glass-border-3-no-overflow" : "glass-border-3"} relative`}>
            <IconComponent size={40} className="text-[#1C1C1E] dark:text-[#F2F2F7]" aria-hidden="true" />
          </DecorativeSceneIcon>

          {hideSceneChrome ? (
            <h1 id="smishing-scene-title" className="sr-only">{currentConfig.title}</h1>
          ) : (
            <>
              <h1 id="smishing-scene-title" className={sceneTitleClassName(layout)}>
                <EditableText configPath="title" placeholder="Enter title">
                  {currentConfig.title}
                </EditableText>
              </h1>
              <p className={sceneSubtitleClassName(layout)}>
                <EditableText configPath="subtitle" placeholder="Enter subtitle">
                  {currentConfig.subtitle}
                </EditableText>
              </p>
            </>
          )}

          <div className={layout.isCard ? CARD_PHONE_STAGE_CLASS : "mt-4 flex w-full justify-center"}>
            <div data-testid="smishing-phone-frame" className={`${layout.isCard ? phoneHeightClass : `relative w-[320px] max-w-full ${phoneHeightClass}`} rounded-[40px] bg-gradient-to-b from-slate-100 to-slate-200 p-3 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.6)] dark:from-slate-800 dark:to-slate-900 dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.1)]`}>
              {/* Dynamic Island */}
              <div className="absolute left-1/2 top-5 z-10 flex h-[24px] w-[80px] -translate-x-1/2 items-center justify-center rounded-full border border-white/20 bg-white/50 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-white/10">
                <div className="h-2 w-2 rounded-full bg-slate-400/60 dark:bg-slate-500/50" />
              </div>

              {/* Side buttons */}
              <div className="absolute -left-0.5 top-28 h-8 w-1 rounded-l-sm bg-slate-300 dark:bg-slate-600" />
              <div className="absolute -left-0.5 top-40 h-14 w-1 rounded-l-sm bg-slate-300 dark:bg-slate-600" />
              <div className="absolute -right-0.5 top-32 h-12 w-1 rounded-r-sm bg-slate-300 dark:bg-slate-600" />

              {/* Screen area */}
              <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[32px] bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-2 pt-10 text-[#1C1C1E] dark:text-[#F2F2F7]">
                  {/* Status bar - positioned after dynamic island space */}
                  <div className="mb-2 flex w-full items-center justify-between px-1 text-[11px] font-medium text-[#1C1C1E] dark:text-[#F2F2F7]">
                    <span>09:41</span>
                    <span className="flex items-center gap-0.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-40" />
                    </span>
                  </div>
                  {currentConfig.channel === "slack" ? (
                    <SlackChat
                      microlearningId={microlearningId}
                      language={(selectedLanguage || "en-gb").toLowerCase()}
                      startLabel={startLabel}
                      workspaceName={currentConfig.workspaceName}
                      channelName={currentConfig.channelName}
                      senderName={currentConfig.senderName}
                      senderNumber={currentConfig.senderNumber}
                      statusTexts={currentConfig.statusTexts}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      otpTexts={otpTexts}
                      landingPageTexts={landingPageTexts}
                      uiTexts={uiTexts}
                      onCompletionChange={setHasCompletedChat}
                      embedded
                      className="flex-1 min-h-0 max-w-none"
                    />
                  ) : currentConfig.channel === "whatsapp" ? (
                    <WhatsAppChat
                      microlearningId={microlearningId}
                      language={(selectedLanguage || "en-gb").toLowerCase()}
                      startLabel={startLabel}
                      senderName={currentConfig.senderName}
                      senderNumber={currentConfig.senderNumber}
                      statusTexts={currentConfig.statusTexts}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      otpTexts={otpTexts}
                      landingPageTexts={landingPageTexts}
                      uiTexts={uiTexts}
                      onCompletionChange={setHasCompletedChat}
                      embedded
                      className="flex-1 min-h-0 max-w-none"
                    />
                  ) : currentConfig.channel === "telegram" ? (
                    <TelegramChat
                      microlearningId={microlearningId}
                      language={(selectedLanguage || "en-gb").toLowerCase()}
                      startLabel={startLabel}
                      senderName={currentConfig.senderName}
                      senderNumber={currentConfig.senderNumber}
                      statusTexts={currentConfig.statusTexts}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      otpTexts={otpTexts}
                      landingPageTexts={landingPageTexts}
                      uiTexts={uiTexts}
                      onCompletionChange={setHasCompletedChat}
                      embedded
                      className="flex-1 min-h-0 max-w-none"
                    />
                  ) : currentConfig.channel === "teams" ? (
                    <TeamsChat
                      microlearningId={microlearningId}
                      language={(selectedLanguage || "en-gb").toLowerCase()}
                      startLabel={startLabel}
                      senderName={currentConfig.senderName}
                      senderNumber={currentConfig.senderNumber}
                      statusTexts={currentConfig.statusTexts}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      otpTexts={otpTexts}
                      landingPageTexts={landingPageTexts}
                      uiTexts={uiTexts}
                      onCompletionChange={setHasCompletedChat}
                      embedded
                      className="flex-1 min-h-0 max-w-none"
                    />
                  ) : currentConfig.channel === "instagram" ? (
                    <InstagramChat
                      microlearningId={microlearningId}
                      language={(selectedLanguage || "en-gb").toLowerCase()}
                      startLabel={startLabel}
                      senderName={currentConfig.senderName}
                      senderNumber={currentConfig.senderNumber}
                      statusTexts={currentConfig.statusTexts}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      otpTexts={otpTexts}
                      landingPageTexts={landingPageTexts}
                      uiTexts={uiTexts}
                      onCompletionChange={setHasCompletedChat}
                      embedded
                      className="flex-1 min-h-0 max-w-none"
                    />
                  ) : currentConfig.channel === "linkedin" ? (
                    <LinkedInChat
                      microlearningId={microlearningId}
                      language={(selectedLanguage || "en-gb").toLowerCase()}
                      startLabel={startLabel}
                      senderName={currentConfig.senderName}
                      senderNumber={currentConfig.senderNumber}
                      statusTexts={currentConfig.statusTexts}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      otpTexts={otpTexts}
                      landingPageTexts={landingPageTexts}
                      uiTexts={uiTexts}
                      onCompletionChange={setHasCompletedChat}
                      embedded
                      className="flex-1 min-h-0 max-w-none"
                    />
                  ) : (
                    <SmishingChat
                      microlearningId={microlearningId}
                      language={(selectedLanguage || "en-gb").toLowerCase()}
                      startLabel={startLabel}
                      senderName={currentConfig.senderName}
                      senderNumber={currentConfig.senderNumber}
                      statusTexts={currentConfig.statusTexts}
                      linkWarningTitle={linkWarningTitle}
                      linkWarningBody={linkWarningBody}
                      otpTexts={otpTexts}
                      landingPageTexts={landingPageTexts}
                      uiTexts={uiTexts}
                      onCompletionChange={setHasCompletedChat}
                      embedded
                      className="flex-1 min-h-0 max-w-none"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {currentConfig.texts?.privacyNotice && (
            <div className={layout.isCard ? "mt-2 max-w-sm shrink-0 rounded-xl bg-black/5 px-3 py-1.5 text-center text-[11px] leading-snug text-[#1C1C1E]/70 dark:bg-white/5 dark:text-[#F2F2F7]/70" : "mt-3 max-w-xl rounded-xl bg-black/5 px-4 py-2 text-xs text-[#1C1C1E]/70 dark:bg-white/5 dark:text-[#F2F2F7]/70"}>
              <EditableText configPath="texts.privacyNotice" placeholder="Privacy notice text">
                {currentConfig.texts.privacyNotice}
              </EditableText>
            </div>
          )}

          {hasCompletedChat && (
            <CallToAction
              text={typeof currentConfig.successCallToActionText === "string" ? currentConfig.successCallToActionText : undefined}
              mobileText={typeof currentConfig.successCallToActionText === "object" ? currentConfig.successCallToActionText.mobile : undefined}
              desktopText={typeof currentConfig.successCallToActionText === "object" ? currentConfig.successCallToActionText.desktop : undefined}
              onClick={onNextSlide}
              disabled={!onNextSlide}
              isRTL={(currentConfig as any)?.isRTL}
            />
          )}
        </main>
      </FontWrapper>
      <CommentPinsOverlay />
    </EditModeProvider>
  );
});
